/**
 * Field-level validation errors arrive from FluentValidation as PascalCase
 * property paths, and are prefixed with the command property when the request
 * wraps the payload (e.g. `Input.EndDate`). Forms address their fields in
 * camelCase, so a naive lookup silently misses every error and the user is left
 * with nothing but a generic toast. Normalizing on both sides fixes that.
 */
export type ApiErrors = Record<string, string[]> | undefined;

export interface NormalizedErrors {
  /** Lower-cased leaf property name → messages. */
  byField: Record<string, string[]>;
  /** Lower-cased key → the original PascalCase leaf, for display. */
  labels: Record<string, string>;
  /** Messages that belong to the request as a whole rather than one field. */
  general: string[];
  count: number;
}

const EMPTY: NormalizedErrors = { byField: {}, labels: {}, general: [], count: 0 };

export function normalizeErrors(errors: ApiErrors): NormalizedErrors {
  if (!errors) return EMPTY;
  const byField: Record<string, string[]> = {};
  const labels: Record<string, string> = {};
  const general: string[] = [];
  let count = 0;

  for (const [rawKey, messages] of Object.entries(errors)) {
    if (!messages?.length) continue;
    count += messages.length;
    // `Input.EndDate` and `EndDate` are the same field to the form.
    const leaf = rawKey.split(".").filter(Boolean).pop() ?? "";
    if (!leaf) {
      general.push(...messages);
      continue;
    }
    const key = leaf.toLowerCase();
    byField[key] = [...(byField[key] ?? []), ...messages];
    labels[key] ??= leaf;
  }

  return { byField, labels, general, count };
}

export function fieldError(
  errors: NormalizedErrors,
  name: string,
): string | undefined {
  return errors.byField[name.toLowerCase()]?.[0];
}

/** Splits a PascalCase property name into a readable label ("EndDate" → "End date"). */
export function humanizeField(name: string): string {
  const spaced = name
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .trim();
  if (!spaced) return name;
  const [first, ...rest] = spaced.split(" ");
  return [
    first.charAt(0).toUpperCase() + first.slice(1),
    ...rest.map((w) => (w === w.toUpperCase() && w.length > 1 ? w : w.toLowerCase())),
  ].join(" ");
}
